import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Mark } from '../../api';
import { getTheme, useThemeMode, FONT_UI } from '../../theme';

const TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTR = '© OpenStreetMap';
/** OSM-plattorna tonas mot designens kartfärger; i mörkt läge inverteras de mot militärgrönt. */
const TILE_FILTER = {
  light: 'saturate(.45) sepia(.18) brightness(1.03)',
  dark: 'invert(.92) hue-rotate(180deg) saturate(.35) sepia(.35) brightness(.82) contrast(.92)',
};

/** Gemensam kartstil: prisnålar, zoomknappar och attribution i designens tokens. */
function MapStyles() {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  return <style>{`
    .qv-map .leaflet-container{background:${T.map};font-family:${FONT_UI}}
    .qv-map .leaflet-tile-pane{filter:${TILE_FILTER[mode]}}
    .qv-pin{background:none;border:none}
    .qv-pin-inner{position:absolute;transform:translate(-50%,-50%);padding:7px 12px;border-radius:999px;background:${T.surface};color:${T.text};font:800 13px ${FONT_UI};white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.2);cursor:pointer;transition:background .15s,color .15s}
    .qv-pin.active .qv-pin-inner,.qv-pin-inner:hover{background:${T.text};color:${T.bg}}
    .qv-map .leaflet-control-zoom{border:none;border-radius:10px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,.15)}
    .qv-map .leaflet-control-zoom a{width:38px;height:38px;line-height:38px;background:${T.surface};color:${T.text};font-size:18px;border-bottom:1px solid ${T.border}}
    .qv-map .leaflet-control-attribution{background:${mode === 'dark' ? 'rgba(47,56,39,.7)' : 'rgba(255,255,255,.7)'};color:${T.textSub};font-size:10px}
    .qv-map .leaflet-control-attribution a{color:${T.textSub}}
  `}</style>;
}

function useTiles(map: L.Map | null) {
  useEffect(() => {
    if (!map) return;
    const layer = L.tileLayer(TILES, { maxZoom: 18, attribution: ATTR }).addTo(map);
    return () => { layer.remove(); };
  }, [map]);
}

function useResize(ref: React.RefObject<HTMLDivElement>, map: L.Map | null) {
  useEffect(() => {
    if (!map || !ref.current) return;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [map, ref]);
}

/** Listvyns karta: en prisnål per jaktmark; hovrat/valt kort ger mörk nål. */
export function ListMap({ marks, pinText, activeId, onPick, searchOnMove, onToggleSearchOnMove, onBounds, height }: {
  marks: Mark[];
  pinText: (m: Mark) => string;
  activeId: string | null;
  onPick: (m: Mark) => void;
  searchOnMove: boolean;
  onToggleSearchOnMove: () => void;
  onBounds: (b: L.LatLngBounds | null) => void;
  height: number | string;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const divRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const fittedKey = useRef('');
  const cb = useRef({ onPick, onBounds, searchOnMove });
  cb.current = { onPick, onBounds, searchOnMove };

  useEffect(() => {
    if (!divRef.current) return;
    const m = L.map(divRef.current, { zoomControl: false, attributionControl: true }).setView([62, 16], 5);
    L.control.zoom({ position: 'topright' }).addTo(m);
    m.on('moveend', () => { if (cb.current.searchOnMove) cb.current.onBounds(m.getBounds()); });
    setMap(m);
    return () => { m.remove(); setMap(null); };
  }, []);
  useTiles(map);
  useResize(divRef, map);

  useEffect(() => { if (map) onBounds(searchOnMove ? map.getBounds() : null); }, [searchOnMove, map]); // eslint-disable-line

  // Nålar: bygg om när listan ändras, passa in kartan när urvalet av marker ändras
  useEffect(() => {
    if (!map) return;
    markersRef.current.forEach(mk => mk.remove());
    markersRef.current.clear();
    const placed = marks.filter(m => m.mapLat != null && m.mapLng != null);
    for (const m of placed) {
      const el = document.createElement('div');
      el.className = 'qv-pin-inner';
      el.textContent = pinText(m);
      const mk = L.marker([m.mapLat!, m.mapLng!], { icon: L.divIcon({ className: 'qv-pin', html: el, iconSize: [0, 0] }), keyboard: true, title: m.name })
        .on('click', () => cb.current.onPick(m)).addTo(map);
      markersRef.current.set(m.id, mk);
    }
    const key = placed.map(m => m.id).sort().join(',');
    if (key && key !== fittedKey.current) {
      fittedKey.current = key;
      const b = L.latLngBounds(placed.map(m => [m.mapLat!, m.mapLng!] as [number, number]));
      map.fitBounds(b, { padding: [50, 50], maxZoom: 10 });
    }
  }, [map, marks, pinText]);

  useEffect(() => {
    markersRef.current.forEach((mk, id) => {
      const on = id === activeId;
      mk.getElement()?.classList.toggle('active', on);
      mk.setZIndexOffset(on ? 1000 : 0);
    });
  }, [activeId, marks, map]);

  return (
    <div className="qv-map" style={{ position: 'relative', height, borderRadius: 16, overflow: 'hidden', border: `1px solid ${T.border}`, background: T.map }}>
      <MapStyles />
      <div ref={divRef} style={{ position: 'absolute', inset: 0 }} />
      <button type="button" onClick={onToggleSearchOnMove} style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 500, padding: '10px 18px', borderRadius: 999, background: T.surface, color: T.text, border: 'none', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,.15)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
        Sök när kartan flyttas {searchOnMove ? '✓' : ''}
      </button>
    </div>
  );
}

/** Jaktsidans karta: streckad orange gräns (coordinatesJson) eller en punkt i markens läge. */
export function BoundaryMap({ mark, height, legend }: { mark: Mark; height: number; legend?: React.ReactNode }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const divRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<L.Map | null>(null);

  useEffect(() => {
    if (!divRef.current) return;
    const m = L.map(divRef.current, { zoomControl: false, scrollWheelZoom: false, attributionControl: true });
    L.control.zoom({ position: 'topright' }).addTo(m);
    setMap(m);
    return () => { m.remove(); setMap(null); };
  }, []);
  useTiles(map);
  useResize(divRef, map);

  useEffect(() => {
    if (!map) return;
    const group = L.featureGroup().addTo(map);
    let fitted = false;
    if (mark.coordinatesJson) {
      try {
        const gj = typeof mark.coordinatesJson === 'string' ? JSON.parse(mark.coordinatesJson) : mark.coordinatesJson;
        const layer = L.geoJSON(gj, {
          style: (f: any) => f?.properties?.type === 'sub_area'
            ? { color: T.amber, weight: 1.5, opacity: 0.7, fillOpacity: 0, dashArray: '4,4' }
            : { color: T.amber, weight: 2.5, opacity: 1, fillColor: T.amber, fillOpacity: 0.14, dashArray: '8,6' },
          pointToLayer: (_f, ll) => L.circleMarker(ll, { radius: 6, color: T.surface, weight: 2, fillColor: T.text, fillOpacity: 1 }),
        }).addTo(group);
        if (layer.getBounds().isValid()) { map.fitBounds(layer.getBounds(), { padding: [30, 30] }); fitted = true; }
      } catch { /* trasig geojson — visa punkten nedan */ }
    }
    if (mark.mapLat != null && mark.mapLng != null) {
      if (!fitted) {
        L.circleMarker([mark.mapLat, mark.mapLng], { radius: 9, color: T.surface, weight: 3, fillColor: T.amber, fillOpacity: 1 }).addTo(group);
        map.setView([mark.mapLat, mark.mapLng], 12);
        fitted = true;
      }
    }
    if (!fitted) map.setView([62, 16], 5);
    return () => { group.remove(); };
  }, [map, mark.id, mark.coordinatesJson, mark.mapLat, mark.mapLng, T.amber, T.surface, T.text]);

  return (
    <div className="qv-map" style={{ position: 'relative', height, borderRadius: 16, overflow: 'hidden', border: `1px solid ${T.border}`, background: T.map }}>
      <MapStyles />
      <div ref={divRef} style={{ position: 'absolute', inset: 0 }} />
      {legend && <div style={{ position: 'absolute', left: 16, bottom: 16, zIndex: 500, display: 'flex', gap: 14, padding: '10px 14px', borderRadius: 10, background: T.surface, color: T.text, fontSize: 12, fontWeight: 600, boxShadow: '0 1px 4px rgba(0,0,0,.12)' }}>{legend}</div>}
    </div>
  );
}
